// «Guida a Politor» (/guida), opened from the user menu: the layout of a
// record read with its index (NewsLayout), the index of the sections in the
// side panel (GuideIndex) and, in the white panel, the way back, the title,
// the introduction and one section per feature with its points, its legend
// where there is one and the links to the screens it describes. Static: the
// texts live in features/guide/content.ts; no api, no state, nothing stored.
import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";

import { ACTION_LINK_CLASS } from "../components/actions";
import { Icon } from "../components/Icon";
import { IngestionIcon } from "../components/IngestionIcon";
import { NewsLayout } from "../components/NewsLayout";
import { BACK_TO_NEWS, ROW_CLASS } from "../components/news/NewsReader";
import { INDEX_TITLE } from "../components/parliament/SectionIndex";
import {
  GUIDE_INTRO,
  GUIDE_SECTIONS,
  GUIDE_TITLE,
  LEGEND_LABEL,
  type GuideSection,
  type LegendItem,
} from "../features/guide/content";
import { GuideIndex } from "../features/guide/GuideIndex";
import { SITE_TITLE } from "../config/site";

const TITLE_ID = "guide-title";
/** The accent text link, for the links of the sections («Vai a …»); the way back is an action button (ACTION_LINK_CLASS). */
const LINK_CLASS = "self-start rounded-sm text-sm leading-6 font-medium text-accent";

export function GuidePage() {
  const { hash } = useLocation();
  const heading = useRef<HTMLHeadingElement>(null);
  // the anchor the page was opened with: with one, the section keeps the scroll and the title does not take the focus
  const openedWithAnchor = useRef(hash.length > 1);

  useEffect(() => {
    const before = document.title;
    document.title = `${GUIDE_TITLE} · ${SITE_TITLE}`;
    return () => {
      document.title = before;
    };
  }, []);

  useEffect(() => {
    if (!openedWithAnchor.current) heading.current?.focus();
  }, []);

  const currentId = hash.length > 1 ? hash.slice(1) : null;
  return (
    <NewsLayout
      labelledBy={TITLE_ID}
      sidebar={{
        title: INDEX_TITLE,
        render: (collapse) => <GuideIndex sections={GUIDE_SECTIONS} currentId={currentId} onCollapse={collapse} />,
      }}
    >
      <div className="flex flex-col gap-[25px]">
        <Link to="/news" className={`${ACTION_LINK_CLASS} self-start`}>
          {BACK_TO_NEWS}
        </Link>
        <div className="flex flex-col gap-[15px]">
          <h1
            id={TITLE_ID}
            ref={heading}
            tabIndex={-1}
            className="text-xl leading-7 font-semibold text-brand outline-none"
          >
            {GUIDE_TITLE}
          </h1>
          <p className="text-sm leading-6 text-muted">{GUIDE_INTRO}</p>
        </div>
      </div>
      {GUIDE_SECTIONS.map((section) => (
        <GuideSectionView key={section.id} section={section} />
      ))}
    </NewsLayout>
  );
}

/** One section: the heading of the lists (NewsSection), the points as the rows of the page of a news, the legend, the links. */
function GuideSectionView({ section }: { section: GuideSection }) {
  const titleId = `${section.id}-title`;
  return (
    <section id={section.id} aria-labelledby={titleId} className="flex flex-col gap-[15px]">
      <h2 id={titleId} className="text-xl leading-7 font-semibold text-brand">
        {section.title}
      </h2>
      <ul className="flex flex-col">
        {section.points.map((point) => (
          <li key={point} className={ROW_CLASS}>
            {point}
          </li>
        ))}
      </ul>
      {section.legend && <Legend items={section.legend} />}
      {section.links.length > 0 && (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {section.links.map((link) => (
            <Link key={link.to} to={link.to} className={LINK_CLASS}>
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

/** The icons with their meaning: the rows of the sources list (sidebar/SourceList.tsx), icon on the left, text on the right. */
function Legend({ items }: { items: readonly LegendItem[] }) {
  return (
    <ul aria-label={LEGEND_LABEL} className="flex flex-col">
      {items.map((item) => (
        <li key={item.key} className="flex items-center gap-3 py-2 text-sm leading-6 text-muted">
          {item.icon === "ingestion" ? <IngestionIcon /> : <Icon name={item.icon} className={item.className} />}
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  );
}
